import React from 'react';
import { Accordion, Badge, Button } from 'react-bootstrap';
import If from '../../component/If';
import { OrderItemRow } from './OrderItemRow';
import { OrderAction } from '../../api/dto/OrderAction';

interface Props {
  order: any;
  viewType: "FLAT" | "GROUPED";
  liveStockMap: { [key: number]: any }; // Güncel stok verisi
  onOrderAction: (action: any, id: number) => void;
  onOrderItemAction: (actionType: any, orderId: number, orderItemId: number, qty: number | null) => void;
  onShowPartialModal: (orderId: number, orderItemId: number, item: any) => void;
}

const BulkApproveButton = ({ order, onAction, hasStockProblem }: { order: any, onAction: any, hasStockProblem: boolean }) => {
  const hasWaitingItems = order.orderItems && order.orderItems.some((oi: any) => oi.statusDescription.name === "WAITING_FOR_APPROVAL");
  const isReview = order.statusDescription?.name === "WAITING_FOR_REVIEW";

  if (isReview || !hasWaitingItems) return null;

  return (
    <Button
      size="sm"
      variant={hasStockProblem ? "outline-danger" : "success"}
      style={{ marginLeft: "10px", padding: "4px 12px", fontSize: "0.8rem", fontWeight: "600" }}
      onClick={(e) => {
        e.stopPropagation();
        if(hasStockProblem && !window.confirm("Bu siparişte stok yetersizliği var. Yine de onaylamak istiyor musunuz?")) return;
        onAction(OrderAction.APPROVE_ALL_ITEMS, order.id);
      }}
    >
      {hasStockProblem ? "⚠️ Riskli Onay" : "✅ Tümünü Onayla"}
    </Button>
  );
};

export const OrderAccordionItem: React.FC<Props> = ({
  order,
  viewType,
  liveStockMap,
  onOrderAction,
  onOrderItemAction,
  onShowPartialModal
}) => {

  const totalAmount = order.amountSummary ? order.amountSummary.totalPriceOnTransaction : 0;
  const formatCurrency = (value: number) => value ? value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";

  // Stok kontrolü
  const hasStockShortage = order.orderItems && order.orderItems.some((item: any) => {
      const currentStock = (liveStockMap && liveStockMap[item.productId]) || item.liveStockInfo;
      return currentStock && currentStock.availableQuantity < 0;
  });

  const itemStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    borderRight: "1px solid #e9ecef",
    paddingLeft: "15px"
  };

  const labelStyle = { fontSize: "0.7rem", color: "#6c757d", marginBottom: "2px", textTransform: "uppercase" as "uppercase", letterSpacing: "0.5px" };
  const valueStyle = { fontSize: "0.9rem", fontWeight: "600", color: "#212529", lineHeight: "1.2" };

  const containerStyle = {
    border: hasStockShortage ? "2px solid #f5c2c7" : "1px solid #dee2e6",
    marginBottom: "8px",
    borderRadius: "6px",
    overflow: "hidden",
    backgroundColor: hasStockShortage ? "#fff5f5" : "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
  };

  return (
    <Accordion.Item eventKey={order.orderNumber} style={containerStyle}>
      <Accordion.Header>
        <div className="w-100 py-2" style={{ display: "flex", alignItems: "center" }}>

          {/* SİPARİŞ GENEL BİLGİLERİ (FLEX YAPISI KORUNDU) */}
          {viewType === "FLAT" && (
            // 1. Flex kapsayıcısına minWidth: 0 ekliyoruz (Çok önemli, yoksa Flexbox genişlemeye devam eder)
            <div style={{ ...itemStyle, borderRight: "none", flex: 1.5, minWidth: 0 }}>
              <span style={labelStyle}>Müşteri / Hedef Depo</span>

              <span style={{ ...valueStyle, color: "#0d6efd", display: "flex", alignItems: "center", gap: "6px" }}>

                {/* Müşteri Adı - Kendi satırında kalması için nowrap verebiliriz */}
                <span style={{ whiteSpace: "nowrap" }}>
                  {order.buyer?.title}
                </span>

                {/* 2. Hedef Depo - text-truncate sınıfı ile sınırlıyoruz */}
                {order.buyerWarehouseComment && (
                  <span
                    className="text-truncate"
                    style={{ color: "#6c757d", fontSize: "0.85em" }}
                    title={order.buyerWarehouseComment} /* 3. Üzerine fareyle gelince uzun halini göstersin */
                  >
                    📍 {order.buyerWarehouseComment}
                  </span>
                )}

              </span>
            </div>
          )}
          <div style={{ ...itemStyle, borderLeft: viewType === "FLAT" ? "1px solid #e9ecef" : "none" }}>
            <span style={labelStyle}>Sipariş No</span>
            <span style={valueStyle}>#{order.orderNumber}</span>
          </div>
          <div style={itemStyle}>
            <span style={labelStyle}>Hedef Tarih</span>
            <span>
              {order.plannedShipmentDate ? (
                <span style={{ fontWeight: "bold", color: "#d63384", fontSize: "0.9rem" }}>
                  {new Date(order.plannedShipmentDate).toLocaleDateString("tr-TR")}
                </span>
              ) : <span className="text-muted">-</span>}
            </span>
          </div>
          <div style={itemStyle}>
            <span style={labelStyle}>Tutar</span>
            <span style={valueStyle}>₺ {formatCurrency(totalAmount)}</span>
          </div>
          <div style={itemStyle}>
            <span style={labelStyle}>Durum</span>
            <div style={{display: 'flex', gap: '5px', alignItems: 'center'}}>
                <Badge bg="info" style={{ fontSize: "0.75rem", padding: "5px 8px", fontWeight: "500" }}>
                  {order.statusDescription?.label}
                </Badge>
                <If value={hasStockShortage}>
                    <Badge bg="danger" className="blink-animation" style={{ fontSize: "0.7rem", padding: "5px 8px", display: "flex", alignItems: "center" }}>
                        <i className="fa fa-exclamation-triangle me-1"></i> Stok Sorunu
                    </Badge>
                </If>
            </div>
          </div>
          <div style={{ paddingLeft: "15px", paddingRight: "15px", display: "flex", alignItems: "center", minWidth: "160px", justifyContent: "flex-end" }}>
            <BulkApproveButton order={order} onAction={onOrderAction} hasStockProblem={hasStockShortage} />
          </div>

        </div>
      </Accordion.Header>

      <Accordion.Body style={{ backgroundColor: "#f8f9fa", padding: "0" }}>

        {/* UYARILAR */}
        <div className="px-3 pt-3">
            <If value={order.statusDescription?.name === "WAITING_FOR_REVIEW"}>
            <div className="alert alert-warning d-flex justify-content-between align-items-center p-2 mb-2">
                <span>⚠️ Bu sipariş inceleme bekliyor.</span>
                <Button size="sm" variant="warning" onClick={() => onOrderAction(OrderAction.FORWARD_TO_IN_REVIEW, order.id)}>İncelemeye Başla</Button>
            </div>
            </If>
            <If value={hasStockShortage}>
                <div className="alert alert-danger p-2 mb-3 d-flex align-items-center" style={{fontSize: "0.85rem", borderLeft: "4px solid #dc3545"}}>
                    <i className="fa fa-exclamation-circle fa-lg me-2"></i>
                    <div><strong>Dikkat!</strong> Bu siparişteki bazı ürünlerde stok yetersizliği tespit edildi.</div>
                </div>
            </If>
        </div>

        {/* 🔥 GÜNCELLENEN TABLO BAŞLIĞI (ROW/COL YAPISI) */}
        {/* Row'daki Grid ile Birebir Aynı: col-md-5 | col-md-2 | col-md-3 | col-md-2 */}
        <div className="row mx-0 py-2 border-bottom border-top text-muted small fw-bold text-uppercase" style={{backgroundColor: "#e9ecef", letterSpacing: "0.5px"}}>
            <div className="col-md-5 ps-4">Ürün Bilgisi</div>
            <div className="col-md-2 text-center">Miktar</div>
            <div className="col-md-3 text-center">Stok Durumu</div>
            <div className="col-md-2 text-end pe-4">Durum / İşlem</div>
        </div>

        {/* ÜRÜN LİSTESİ */}
        <div className="bg-white">
            {order.orderItems && order.orderItems.map((orderItem: any) => {
                const currentStock = (liveStockMap && liveStockMap[orderItem.productId]) || orderItem.liveStockInfo;
                return (
                  <OrderItemRow
                    key={orderItem.id}
                    item={orderItem}
                    liveStockInfo={currentStock}
                    order={order}
                    onAction={onOrderItemAction}
                    onShowPartialModal={onShowPartialModal}
                  />
                );
            })}
        </div>

        {/* SİPARİŞ NOTU */}
        <If value={order.description}>
          <div className="p-3">
            <div style={{ padding: "10px", backgroundColor: "#fff3cd", borderLeft: "4px solid #ffc107", borderRadius: "4px", color: "#856404", fontSize: "0.9rem" }}>
                <strong className="d-block mb-1"><i className="fa fa-sticky-note-o me-1"></i> Sipariş Notu:</strong>
                {order.description}
            </div>
          </div>
        </If>

      </Accordion.Body>
    </Accordion.Item>
  );
};